import type { GroupDocument } from "@kekkle/shared/types/documents/group-document/group-document";
import type { UserDocument } from "@kekkle/shared/types/documents/user-document/user-document";
import type { User, IdTokenResult } from "firebase/auth";
import type { WorldeScoreDocument } from "@kekkle/shared/types/documents/wordle-score-document/wordle-score-document";
import type { WordleMonthlyScoreDocument } from "@kekkle/shared/types/documents/wordle-monthly-score-document/wordle-monthly-score-document";

/**
 * Safely sets an item in localStorage
 * @param key The localStorage key
 * @param value The value to store (will be stringified if object)
 */
export function setLocalStorageItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Silently fail - localStorage might be full or disabled
  }
}

/**
 * Safely gets and parses an item from localStorage
 * @param key The localStorage key
 * @param defaultValue The default value to return if parsing fails or item doesn't exist
 * @returns The parsed value or defaultValue
 */
export function getLocalStorageItem<T>(key: string, defaultValue: T): T {
  try {
    const item = localStorage.getItem(key);
    if (item === null) {
      return defaultValue;
    }
    return JSON.parse(item) as T;
  } catch {
    // Return default on any error (invalid JSON, etc.)
    return defaultValue;
  }
}

/**
 * Safely gets user groups from localStorage as a Map
 * Handles invalid JSON, missing data, and malformed structures
 * @returns Map of group IDs to GroupDocuments, or empty Map on error
 */
/**
 * Convert ISO date strings back to Date objects for fields that are
 * serialized as strings by JSON.stringify (Date → string in localStorage).
 */
function reviveGroupDates(group: Record<string, unknown>): GroupDocument {
  const dateFields = [
    "created_at",
    "updated_at",
    "join_token_timestamp",
    "premium_till",
  ] as const;

  for (const field of dateFields) {
    if (typeof group[field] === "string") {
      group[field] = new Date(group[field] as string);
    }
  }

  const members = group.members as
    | Record<string, Record<string, unknown>>
    | undefined;
  if (members) {
    for (const member of Object.values(members)) {
      if (typeof member.birthday === "string") {
        member.birthday = new Date(member.birthday);
      }
    }
  }

  return group as unknown as GroupDocument;
}

export function getGroupsFromLocalStorage(): Map<string, GroupDocument> {
  try {
    const item = localStorage.getItem("userGroups");
    if (item === null) {
      return new Map();
    }

    const parsed = JSON.parse(item);

    // Validate that parsed is an object (not array, null, etc.)
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      return new Map();
    }

    // Convert object to Map and revive date fields
    const entries = Object.entries(parsed).map(
      ([key, value]) =>
        [key, reviveGroupDates(value as Record<string, unknown>)] as [
          string,
          GroupDocument,
        ],
    );
    return new Map(entries);
  } catch {
    // Return empty Map on any error (invalid JSON, etc.)
    return new Map();
  }
}

/**
 * Safely stores user groups Map to localStorage
 * @param groups Map of group IDs to GroupDocuments
 */
export function setGroupsInLocalStorage(
  groups: Map<string, GroupDocument>,
): void {
  try {
    const groupsObject = Object.fromEntries(groups);
    localStorage.setItem("userGroups", JSON.stringify(groupsObject));
  } catch {
    // Silently fail - localStorage might be full or disabled
  }
}

/**
 * Safely gets Firebase auth user from localStorage
 * Handles invalid JSON and malformed data
 * @returns User object or null
 */
export function getFirebaseAuthUserFromLocalStorage(): User | null {
  try {
    const item = localStorage.getItem("firebaseAuthUser");
    if (item === null) {
      return null;
    }

    const parsed = JSON.parse(item);

    // If explicitly null, return null
    if (parsed === null) {
      return null;
    }

    // Validate that parsed is an object with uid (basic User validation)
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      Array.isArray(parsed) ||
      !parsed.uid
    ) {
      return null;
    }

    return parsed as User;
  } catch {
    // Return null on any error (invalid JSON, etc.)
    return null;
  }
}

/**
 * Safely stores Firebase auth user to localStorage
 * @param user Firebase User object or null
 */
export function setFirebaseAuthUserInLocalStorage(user: User | null): void {
  try {
    localStorage.setItem("firebaseAuthUser", JSON.stringify(user));
  } catch {
    // Silently fail - localStorage might be full or disabled
  }
}

/**
 * Safely gets Firebase auth ID token from localStorage
 * Handles invalid JSON and malformed data
 * @returns IdTokenResult object or null
 */
export function getFirebaseAuthIdTokenFromLocalStorage(): IdTokenResult | null {
  try {
    const item = localStorage.getItem("firebaseAuthIdToken");
    if (item === null) {
      return null;
    }

    const parsed = JSON.parse(item);

    // If explicitly null, return null
    if (parsed === null) {
      return null;
    }

    // Validate that parsed is an object with token (basic IdTokenResult validation)
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      Array.isArray(parsed) ||
      !parsed.token
    ) {
      return null;
    }

    return parsed as IdTokenResult;
  } catch {
    // Return null on any error (invalid JSON, etc.)
    return null;
  }
}

/**
 * Safely stores Firebase auth ID token to localStorage
 * @param token IdTokenResult object or null
 */
export function setFirebaseAuthIdTokenInLocalStorage(
  token: IdTokenResult | null,
): void {
  try {
    localStorage.setItem("firebaseAuthIdToken", JSON.stringify(token));
  } catch {
    // Silently fail - localStorage might be full or disabled
  }
}

/**
 * Safely gets user document from localStorage
 * Handles invalid JSON and malformed data
 * @returns UserDocument object or null
 */
export function getUserDataFromLocalStorage(): UserDocument | null {
  try {
    const item = localStorage.getItem("userData");
    if (item === null) {
      return null;
    }

    const parsed = JSON.parse(item);

    // If explicitly null, return null
    if (parsed === null) {
      return null;
    }

    // Validate that parsed is an object with _id (basic UserDocument validation)
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      Array.isArray(parsed) ||
      !parsed._id
    ) {
      return null;
    }

    return parsed as UserDocument;
  } catch {
    // Return null on any error (invalid JSON, etc.)
    return null;
  }
}

/**
 * Safely stores user document to localStorage
 * @param userData UserDocument object or null
 */
export function setUserDataInLocalStorage(userData: UserDocument | null): void {
  try {
    localStorage.setItem("userData", JSON.stringify(userData));
  } catch {
    // Silently fail - localStorage might be full or disabled
  }
}

/**
 * Safely gets wordle game IDs from localStorage
 * Handles invalid JSON and malformed data
 * @returns Record of date strings to game IDs, or empty object
 */
export function getWordleGameIdsFromLocalStorage(): Record<string, string> {
  try {
    const item = localStorage.getItem("wordleGameIds");
    if (item === null) {
      return {};
    }

    const parsed = JSON.parse(item);

    // Validate that parsed is a plain object (not array, null, etc.)
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      return {};
    }

    // Validate all values are strings
    const entries = Object.entries(parsed);
    for (const [key, value] of entries) {
      if (typeof key !== "string" || typeof value !== "string") {
        return {};
      }
    }

    return parsed as Record<string, string>;
  } catch {
    // Return empty object on any error (invalid JSON, etc.)
    return {};
  }
}

/**
 * Safely stores wordle game IDs to localStorage
 * @param gameIds Record of date strings to game IDs
 */
export function setWordleGameIdsInLocalStorage(
  gameIds: Record<string, string>,
): void {
  try {
    localStorage.setItem("wordleGameIds", JSON.stringify(gameIds));
  } catch {
    // Silently fail - localStorage might be full or disabled
  }
}

/**
 * Safely gets wordle scores from localStorage
 * Handles invalid JSON and malformed data
 * @returns Record of game IDs to score arrays, or empty object
 */
export function getWordleScoresFromLocalStorage(): Record<
  string,
  WorldeScoreDocument[]
> {
  try {
    const item = localStorage.getItem("wordleScores");
    if (item === null) {
      return {};
    }

    const parsed = JSON.parse(item);

    // Validate that parsed is a plain object (not array, null, etc.)
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      return {};
    }

    // Validate all values are arrays
    const entries = Object.entries(parsed);
    for (const [, value] of entries) {
      if (!Array.isArray(value)) {
        return {};
      }
    }

    return parsed as Record<string, WorldeScoreDocument[]>;
  } catch {
    // Return empty object on any error (invalid JSON, etc.)
    return {};
  }
}

/**
 * Safely stores wordle scores to localStorage
 * @param scores Record of game IDs to score arrays
 */
export function setWordleScoresInLocalStorage(
  scores: Record<string, WorldeScoreDocument[]>,
): void {
  try {
    localStorage.setItem("wordleScores", JSON.stringify(scores));
  } catch {
    // Silently fail - localStorage might be full or disabled
  }
}

/**
 * Safely gets boolean flag for "only known users" from localStorage
 * @param key The localStorage key to read from
 * @param defaultValue The default value if not found or invalid
 * @returns Boolean value
 */
export function getBooleanFromLocalStorage(
  key: string,
  defaultValue: boolean,
): boolean {
  try {
    const item = localStorage.getItem(key);
    if (item === null) {
      return defaultValue;
    }

    const parsed = JSON.parse(item);

    // Validate that parsed is a boolean
    if (typeof parsed !== "boolean") {
      return defaultValue;
    }

    return parsed;
  } catch {
    // Return default on any error (invalid JSON, etc.)
    return defaultValue;
  }
}

/**
 * Safely gets wordle monthly scores from localStorage
 * Handles invalid JSON and malformed data
 * @returns Record of year-month keys to score arrays, or empty object
 */
export function getWordleMonthlyScoresFromLocalStorage(): Record<
  string,
  WordleMonthlyScoreDocument[]
> {
  try {
    const item = localStorage.getItem("wordleMonthlyScores");
    if (item === null) {
      return {};
    }

    const parsed = JSON.parse(item);

    // Validate that parsed is a plain object (not array, null, etc.)
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      return {};
    }

    // Validate all values are arrays
    const entries = Object.entries(parsed);
    for (const [, value] of entries) {
      if (!Array.isArray(value)) {
        return {};
      }
    }

    return parsed as Record<string, WordleMonthlyScoreDocument[]>;
  } catch {
    // Return empty object on any error (invalid JSON, etc.)
    return {};
  }
}

/**
 * Safely stores wordle monthly scores to localStorage
 * @param scores Record of year-month keys to score arrays
 */
export function setWordleMonthlyScoresInLocalStorage(
  scores: Record<string, WordleMonthlyScoreDocument[]>,
): void {
  try {
    localStorage.setItem("wordleMonthlyScores", JSON.stringify(scores));
  } catch {
    // Silently fail - localStorage might be full or disabled
  }
}

/**
 * Safely gets wordle monthly scores (only known users) from localStorage
 * Handles invalid JSON and malformed data
 * @returns Record of year-month keys to score arrays, or empty object
 */
export function getWordleMonthlyScoresOnlyKnownUsersFromLocalStorage(): Record<
  string,
  WordleMonthlyScoreDocument[]
> {
  try {
    const item = localStorage.getItem("wordleMonthlyScoresOnlyKnownUsers");
    if (item === null) {
      return {};
    }

    const parsed = JSON.parse(item);

    // Validate that parsed is a plain object (not array, null, etc.)
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      return {};
    }

    // Validate all values are arrays
    const entries = Object.entries(parsed);
    for (const [, value] of entries) {
      if (!Array.isArray(value)) {
        return {};
      }
    }

    return parsed as Record<string, WordleMonthlyScoreDocument[]>;
  } catch {
    // Return empty object on any error (invalid JSON, etc.)
    return {};
  }
}

/**
 * Safely stores wordle monthly scores (only known users) to localStorage
 * @param scores Record of year-month keys to score arrays
 */
export function setWordleMonthlyScoresOnlyKnownUsersInLocalStorage(
  scores: Record<string, WordleMonthlyScoreDocument[]>,
): void {
  try {
    localStorage.setItem(
      "wordleMonthlyScoresOnlyKnownUsers",
      JSON.stringify(scores),
    );
  } catch {
    // Silently fail - localStorage might be full or disabled
  }
}

/**
 * Clears all wordle-related localStorage data
 * Useful for fixing corrupted data or after structure changes
 */
export function clearWordleLocalStorage(): void {
  try {
    localStorage.removeItem("wordleGameIds");
    localStorage.removeItem("wordleScores");
    localStorage.removeItem("wordleMonthlyScores");
    localStorage.removeItem("wordleMonthlyScoresOnlyKnownUsers");
    localStorage.removeItem("onlyKnownUsersWorldeScores");
    localStorage.removeItem("onlyKnownUsersWordleMonthlyScores");
  } catch {
    // Silently fail - localStorage might be disabled
  }
}

/**
 * Safely gets active group ID from localStorage
 * @returns active group ID string or null
 */
export function getActiveGroupIdFromLocalStorage(): string | null {
  try {
    const item = localStorage.getItem("activeGroupId");
    if (
      item === null ||
      item === "null" ||
      item === "undefined" ||
      item === ""
    ) {
      return null;
    }
    return item;
  } catch {
    return null;
  }
}

/**
 * Safely stores active group ID to localStorage
 * @param groupId Active group ID string or null
 */
export function setActiveGroupIdInLocalStorage(groupId: string | null): void {
  try {
    if (groupId === null || groupId === "") {
      localStorage.removeItem("activeGroupId");
    } else {
      localStorage.setItem("activeGroupId", groupId);
    }
  } catch {
    // Silently fail - localStorage might be full or disabled
  }
}
